/**
 * AIDA Freediving Competition Rules 2025
 * Based on official AIDA documents:
 * - Rules & Regulations 2025 (Version 17.7, January 2025)
 * - Guidelines and Protocols for Deep Freediving (Version 2.1)
 */

export const AIDA_RULES = {
  version: "17.7",
  date: "2025-01-31",
  organization: "AIDA",
  
  // ============================================
  // DISCIPLINES
  // ============================================
  disciplines: {
    // Pool Disciplines
    pool: {
      STA: {
        name: "Static Apnea",
        code: "STA",
        type: "pool",
        description: "Hold breath for as long as possible with airway submerged",
        measurementUnit: "time", // seconds
        pointsPerUnit: 0.2, // 1 second = 0.2 points
        rounding: "down_to_0.2", // Round down to nearest 0.2 points
        equipment: {
          fins: false,
          mask: "optional",
          noseClip: "optional",
          wetsuit: "optional"
        },
        minInterval: 840 // 14 minutes between OT
      },
      DYN: {
        name: "Dynamic Apnea With Fins",
        code: "DYN",
        type: "pool",
        description: "Swim underwater for maximum distance using fins or monofin",
        measurementUnit: "distance", // meters
        pointsPerUnit: 0.5, // 1 meter = 0.5 points
        rounding: "down_to_0.5", // Round down to nearest 0.5 points
        equipment: {
          fins: "monofin_or_bifins",
          mask: "optional",
          noseClip: "optional",
          wetsuit: "optional"
        },
        minInterval: 600 // 10 minutes between OT
      },
      DYNB: {
        name: "Dynamic Apnea With Bifins",
        code: "DYNB",
        type: "pool",
        description: "Swim underwater for maximum distance using bi-fins only",
        measurementUnit: "distance", // meters
        pointsPerUnit: 0.5, // 1 meter = 0.5 points
        rounding: "down_to_0.5", // Round down to nearest 0.5 points
        equipment: {
          fins: "bifins_only",
          mask: "optional",
          noseClip: "optional",
          wetsuit: "optional"
        },
        minInterval: 600 // 10 minutes between OT
      },
      DNF: {
        name: "Dynamic Apnea No Fins",
        code: "DNF",
        type: "pool",
        description: "Swim underwater for maximum distance without fins",
        measurementUnit: "distance", // meters
        pointsPerUnit: 0.5, // 1 meter = 0.5 points
        rounding: "down_to_0.5", // Round down to nearest 0.5 points
        equipment: {
          fins: false,
          mask: "optional",
          noseClip: "optional",
          wetsuit: "optional"
        },
        minInterval: 600 // 10 minutes between OT
      }
    },
    
    // Depth Disciplines
    depth: {
      CWT: {
        name: "Constant Weight With Fins",
        code: "CWT",
        type: "depth",
        description: "Descend and ascend to target depth using fins/monofin and arms. Cannot pull on the line.",
        measurementUnit: "depth", // meters
        pointsPerUnit: 1.0, // 1 meter = 1.0 point
        rounding: "down_to_1.0", // Round down to nearest point
        canPullRope: false,
        equipment: {
          fins: "monofin_or_bifins",
          mask: "required",
          noseClip: "optional",
          wetsuit: "required",
          lanyard: "required"
        }
      },
      CWTB: {
        name: "Constant Weight With Bifins",
        code: "CWTB",
        type: "depth",
        description: "Descend and ascend to target depth using bi-fins only. Cannot pull on the line.",
        measurementUnit: "depth", // meters
        pointsPerUnit: 1.0, // 1 meter = 1.0 point
        rounding: "down_to_1.0",
        canPullRope: false,
        equipment: {
          fins: "bifins_only",
          mask: "required",
          noseClip: "optional",
          wetsuit: "required",
          lanyard: "required"
        }
      },
      CNF: {
        name: "Constant Weight No Fins",
        code: "CNF",
        type: "depth",
        description: "Descend and ascend to target depth without fins. Cannot pull on the line.",
        measurementUnit: "depth", // meters
        pointsPerUnit: 1.0, // 1 meter = 1.0 point
        rounding: "down_to_1.0",
        canPullRope: false,
        equipment: {
          fins: false,
          mask: "required",
          noseClip: "optional",
          wetsuit: "required",
          lanyard: "required"
        }
      },
      FIM: {
        name: "Free Immersion",
        code: "FIM",
        type: "depth",
        description: "Descend and ascend to target depth by pulling on the rope.",
        measurementUnit: "depth", // meters
        pointsPerUnit: 1.0, // 1 meter = 1.0 point
        rounding: "down_to_1.0",
        canPullRope: true,
        equipment: {
          fins: false,
          mask: "required",
          noseClip: "optional",
          wetsuit: "required",
          lanyard: "required"
        }
      },
      VWT: {
        name: "Variable Weight",
        code: "VWT",
        type: "depth",
        description: "Descend with a weighted sled, ascend with own muscle strength. Record attempts only.",
        measurementUnit: "depth", // meters
        pointsPerUnit: 1.0,
        rounding: "down_to_1.0",
        canPullRope: true,
        recordAttemptOnly: true,
        equipment: {
          sled: "required",
          mask: "required",
          wetsuit: "required",
          lanyard: "required"
        }
      }
    }
  },

  // ============================================
  // TIMING RULES
  // ============================================
  timing: {
    countdown: {
      // Standard countdown sequence (English)
      sequence: [
        { time: -120, announcement: "Two minutes" },
        { time: -60, announcement: "One minute" },
        { time: -30, announcement: "Thirty seconds" },
        { time: -20, announcement: "Twenty seconds" },
        { time: -10, announcement: "Ten seconds" },
        { time: -5, announcement: "Five" },
        { time: -4, announcement: "Four" },
        { time: -3, announcement: "Three" },
        { time: -2, announcement: "Two" },
        { time: -1, announcement: "One" },
        { time: 0, announcement: "Official Top" }
      ]
    },
    startWindow: {
      pool: {
        noPenalty: 10,        // 10 seconds after OT without penalty
        maxAllowed: 30,       // DQ after 30 seconds
        earlyStartDQ: true
      },
      depth: {
        noPenalty: 10,        // 10 seconds after OT without penalty  
        maxAllowed: 30,       // DQ after 30 seconds
        earlyStartDQ: true
      }
    },
    // Minimum intervals between OT times
    minIntervals: {
      STA: 840,      // 14 minutes
      DYN: 600,      // 10 minutes
      DYNB: 600,     // 10 minutes
      DNF: 600,      // 10 minutes
      // Depth intervals based on announced depth
      depth: {
        over100m: 600,    // 10 minutes
        "80-100m": 540,   // 9 minutes
        "50-80m": 480,    // 8 minutes
        under50m: 420     // 7 minutes
      }
    },
    surfaceProtocol: {
      timeLimit: 15.0, // 15 seconds to complete surface protocol
      tasks: [
        "Remove all equipment covering eyes and/or airway",
        "Give one visible OK-signal (hand signal)",
        "Give one verbal OK-signal ('I'm OK' or 'I am OK')"
      ],
      order: "Any order",
      restrictions: [
        "No extraneous visual or verbal cues until SP completed",
        "Face wipe and hood/swim cap removal allowed"
      ]
    },
    recommendedZoneTime: 180 // At least 3 minutes in performance zone recommended
  },

  // ============================================
  // CARD SYSTEM
  // ============================================
  cards: {
    WHITE: {
      code: "WHITE",
      meaning: "Performance OK - Full points awarded",
      validatesRecord: true,
      color: "#FFFFFF"
    },
    YELLOW: {
      code: "YELLOW",
      meaning: "Performance OK with penalties",
      validatesRecord: false,
      color: "#FFD700"
    },
    RED: {
      code: "RED",
      meaning: "Disqualification - Zero points",
      validatesRecord: false,
      color: "#FF0000"
    }
  },

  // ============================================
  // PENALTIES (Point Deductions)
  // ============================================
  penalties: {
    // Early Start Penalty
    EARLY_START: {
      code: "EARLY_START",
      description: "Starting before Official Top (OT)",
      penalty: 1, // 1 point per 5-second unit
      unit: "points",
      perUnit: 5, // per 5 seconds
      calculation: "1 point per unit of 5 seconds before OT"
    },
    
    // Late Start Penalty
    LATE_START: {
      code: "LATE_START",
      description: "Starting after the 10-second window",
      penalty: 1, // 1 point per 5-second unit
      unit: "points",
      perUnit: 5, // per 5 seconds after 10-second window
      maxBeforeDQ: 30, // DQ after 30 seconds
      calculation: "1 point per unit of 5 seconds after 10-second window"
    },
    
    // Under Announced Performance (RP < AP)
    UNDER_AP: {
      STA: {
        code: "UNDER_AP_STA",
        description: "Realized performance less than announced (Static)",
        penalty: 0.2, // 0.2 points per second
        unit: "points",
        perUnit: 1, // per second
        calculation: "0.2 points per second difference between AP and RP"
      },
      DYN: {
        code: "UNDER_AP_DYN",
        description: "Realized performance less than announced (Dynamic)",
        penalty: 0.5, // 0.5 points per meter
        unit: "points",
        perUnit: 1, // per meter
        calculation: "0.5 points per meter difference between AP and RP",
        appliesTo: ["DYN", "DYNB", "DNF"]
      },
      DEPTH: {
        code: "UNDER_AP_DEPTH",
        description: "Realized depth less than announced (Depth disciplines)",
        penalty: 1, // 1 point per meter
        unit: "points",
        perUnit: 1, // per meter
        calculation: "1 point per meter shallower than announced",
        appliesTo: ["CWT", "CWTB", "CNF", "FIM"]
      }
    },
    
    // Start/Turn Penalties (Pool)
    START_TURN: {
      code: "START_TURN",
      description: "Body/equipment doesn't touch end wall during start/turn",
      penalty: 5,
      unit: "points",
      perOccurrence: true,
      appliesTo: ["DYN", "DYNB", "DNF"]
    },
    
    // Pull Penalty (Pool)
    PULL_POOL: {
      code: "PULL_POOL",
      description: "Pulling/propelling on support point before exiting water",
      penalty: 5,
      unit: "points",
      perOccurrence: true,
      appliesTo: ["DYN", "DYNB", "DNF"]
    },
    
    // Lanyard Penalty (Depth)
    LANYARD: {
      code: "LANYARD",
      description: "Removing safety lanyard during performance (except safety)",
      penalty: 10,
      unit: "points",
      appliesTo: ["CWT", "CWTB", "CNF", "FIM"]
    },
    
    // Tag Penalty (Depth)
    TAG: {
      code: "TAG",
      description: "Failing to deliver tag to jury",
      penalty: 1,
      unit: "points",
      appliesTo: ["CWT", "CWTB", "CNF", "FIM"]
    },
    
    // Grab Penalty (Depth - CWT/CWTB/CNF only)
    GRAB: {
      code: "GRAB",
      description: "Grabbing the line (except permitted touches)",
      penalty: 5,
      unit: "points",
      perOccurrence: true,
      appliesTo: ["CWT", "CWTB", "CNF"]
    }
  },

  // ============================================
  // DISQUALIFICATIONS (DQ)
  // ============================================
  disqualifications: {
    DQSP: {
      code: "DQSP",
      description: "Failed to complete Surface Protocol correctly within 15 seconds"
    },
    DQBO: {
      code: "DQBO",
      description: "Blackout - Loss of consciousness, cardiac arrest, or involuntary respiratory arrest"
    },
    DQAIRWAYS: {
      code: "DQAIRWAYS",
      description: "Airways dip below surface after surfacing before jury verdict"
    },
    DQTOUCH: {
      code: "DQTOUCH",
      description: "Unauthorized touching of the athlete"
    },
    DQLATESTART: {
      code: "DQLATESTART",
      description: "Starting more than 30 seconds after Official Top"
    },
    DQPULL: {
      code: "DQPULL",
      description: "Pulling the line outside permitted exemptions (CWT/CWTB/CNF)"
    },
    DQOTHER: {
      code: "DQOTHER",
      description: "Various infractions including prohibited devices, coach immersion, turn violations, etc."
    }
  },

  // ============================================
  // PERFORMANCE CALCULATIONS
  // ============================================
  calculations: {
    // Static Apnea (STA)
    static: {
      formula: "Points = Time (seconds) × 0.2",
      rounding: "Round down to nearest 0.2 points",
      measurement: "Average of two timekeeper times, registered in whole seconds",
      example: {
        timekeeper1: 308.64,  // 5'08"64
        timekeeper2: 307.48,  // 5'07"48
        average: 308.06,      // 5'08"06
        registeredTime: 308,  // 5'08" (whole seconds)
        points: 61.6          // 308 × 0.2 = 61.6
      }
    },
    
    // Dynamic Apnea (DYN, DYNB, DNF)
    dynamic: {
      formula: "Points = Distance (meters) × 0.5",
      rounding: "Round distance down to whole meter, then round points to nearest 0.5",
      measurement: "Distance measured at surfacing point",
      example: {
        measuredDistance: 172.9,
        registeredDistance: 172, // rounded down
        points: 86.0             // 172 × 0.5 = 86.0
      }
    },
    
    // Depth Disciplines (CWT, CWTB, CNF, FIM)
    depth: {
      formula: "Points = Depth (meters) × 1.0",
      rounding: "Round down to nearest point (whole meter)",
      measurement: "Official depth gauge reading",
      tagRequired: true,
      example: {
        announcedDepth: 50,
        realizedDepth: 46.5,
        registeredDepth: 46,    // rounded down
        underAPPenalty: 4,      // 50 - 46 = 4 meters short
        tagPenalty: 0,          // tag delivered
        points: 42              // 46 - 4 = 42 points
      }
    },
    
    // Team World Championships scoring (different coefficients)
    teamWorldChampionships: {
      STA: 0.2,  // 1 second = 0.2 points
      DYN: 0.4,  // 1 meter = 0.4 points (note: different from individual)
      CWT: 1.0   // 1 meter = 1.0 points
    }
  },

  // ============================================
  // WORLD RECORD REQUIREMENTS
  // ============================================
  recordRequirements: {
    general: {
      notification: "3 weeks advance notice to organizer",
      improvement: {
        STA: 1,    // Must exceed current record by 1 second
        DYN: 1,    // Must exceed by 1 meter
        DYNB: 1,
        DNF: 1,
        CWT: 1,    // Must exceed by 1 meter
        CWTB: 1,
        CNF: 1,
        FIM: 1
      },
      equalPerformance: "Shared record if equal to existing"
    },
    pool: {
      judges: "At least one AIDA Managing Judge (Level B or higher)",
      nationalityRequirement: "Managing Judge must be different nationality than athlete",
      exception: "World Championships - any elected jury member of different nationality"
    },
    depth: {
      judges: "Two AIDA judges (Level D or higher)",
      managingJudge: "At least one Managing Judge (Level B or higher) of different nationality",
      positioning: "Both judges must be in the water"
    },
    validation: "White card required - no penalties allowed"
  },

  // ============================================
  // WORLD RECORD ATTEMPT (WRA) REQUIREMENTS
  // ============================================
  worldRecordAttempt: {
    maxPerDay: 1, // Maximum 1 WR attempt per day for depth
    prerequisites: [
      "Valid medical certificate",
      "Safety and evacuation plan",
      "Athlete profile submitted"
    ],
    timeframe: {
      STA: {
        withinRange: 30, // Within 30 seconds of current WR
        notExceeding: "3 months from event start"
      }
    },
    jury: {
      required: [
        "Managing Judge (Level B or higher)",
        "Second judge (senior, Level D or higher)"
      ],
      nationalityRequirement: "Different nationalities required"
    }
  },

  // ============================================
  // DEEP FREEDIVING SAFETY PROTOCOLS
  // ============================================
  deepFreedivingSafety: {
    risks: {
      DCI: "Decompression Illness - primary concern at depth",
      lungSqueeze: "Barotrauma risk beyond residual volume",
      oxygenToxicity: "Risk when using oxygen below 5m",
      nitrogenNarcosis: "Impairment at depth"
    },
    oxygenProtocol: {
      postDive: {
        mandatory: true,
        optimalDepth: 5, // meters
        minDuration: 5,  // minutes
        maxDuration: 60, // minutes
        maxDepthForOxygen: 12, // Never use oxygen deeper than 12m
        durationBelow5m: 10   // Max 10 mins below 5m, then ascend
      },
      supervision: "Direct supervision by trained safety diver required",
      equipment: "Full-face mask recommended for managing oxygen toxicity"
    },
    safetyDivers: {
      required: true,
      training: "Must be trained in freediving rescue",
      positioning: "Readily available for immediate intervention"
    },
    trainingRestrictions: {
      deepDivesPerDay: 1, // Maximum 1 deep training dive per day
      gradualProgression: "Required - avoid rapid depth increases"
    },
    emergency: {
      DCISuspected: [
        "Alert support staff immediately",
        "Begin oxygen administration",
        "Descend to 9-12m on oxygen if safe",
        "Ascend to 5m for 40+ minutes",
        "Continuous oxygen during transport"
      ],
      convulsions: [
        "Maintain airway",
        "Secure mouthpiece (don't replace if lost)",
        "Bring to surface safely"
      ]
    }
  },

  // ============================================
  // ATHLETE CATEGORIES
  // ============================================
  categories: {
    junior: {
      minAge: 16,
      maxAge: 17
    },
    senior: {
      minAge: 18,
      maxAge: null
    },
    masters: {
      brackets: [
        { code: "M40", minAge: 40, maxAge: 49 },
        { code: "M50", minAge: 50, maxAge: 59 },
        { code: "M60", minAge: 60, maxAge: 69 },
        { code: "M70", minAge: 70, maxAge: null }
      ]
    },
    gender: ["male", "female"]
  }
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Calculate points for Static Apnea (STA)
 * @param {number} timeSeconds - Time in seconds
 * @param {number} announcedTime - Announced performance in seconds (for penalty calculation)
 * @returns {object} Result with breakdown
 */
export const calculateSTAPoints = (timeSeconds, announcedTime = null) => {
  const registeredTime = Math.floor(timeSeconds);
  let points = registeredTime * 0.2;
  
  // Round down to nearest 0.2
  points = Math.floor(points * 5) / 5;
  
  let penalty = 0;
  if (announcedTime && registeredTime < announcedTime) {
    const difference = announcedTime - registeredTime;
    penalty = difference * 0.2;
    penalty = Math.floor(penalty * 5) / 5;
  }
  
  return {
    registeredTime,
    basePoints: points,
    underAPPenalty: penalty,
    finalPoints: Math.max(0, points - penalty)
  };
};

/**
 * Calculate points for Dynamic disciplines (DYN, DYNB, DNF)
 * @param {number} distanceMeters - Distance in meters
 * @param {number} announcedDistance - Announced performance in meters (for penalty calculation)
 * @returns {object} Result with breakdown
 */
export const calculateDYNPoints = (distanceMeters, announcedDistance = null) => {
  const registeredDistance = Math.floor(distanceMeters);
  let points = registeredDistance * 0.5;
  
  // Round down to nearest 0.5
  points = Math.floor(points * 2) / 2;
  
  let penalty = 0;
  if (announcedDistance && registeredDistance < announcedDistance) {
    const difference = announcedDistance - registeredDistance;
    penalty = difference * 0.5;
  }
  
  return {
    registeredDistance,
    basePoints: points,
    underAPPenalty: penalty,
    finalPoints: Math.max(0, points - penalty)
  };
};

/**
 * Calculate points for Depth disciplines (CWT, CWTB, CNF, FIM)
 * @param {number} depthMeters - Actual depth reached in meters
 * @param {number} announcedDepth - Announced depth in meters
 * @param {boolean} tagDelivered - Whether tag was delivered to jury
 * @param {number} additionalPenalties - Any other penalty points
 * @returns {object} Result with breakdown
 */
export const calculateDepthPoints = (depthMeters, announcedDepth, tagDelivered = true, additionalPenalties = 0) => {
  const registeredDepth = Math.floor(depthMeters);
  let basePoints = registeredDepth; // 1 meter = 1 point
  
  // Under AP penalty
  let underAPPenalty = 0;
  if (registeredDepth < announcedDepth) {
    underAPPenalty = announcedDepth - registeredDepth;
  }
  
  // Tag penalty
  const tagPenalty = tagDelivered ? 0 : 1;
  
  const totalPenalty = underAPPenalty + tagPenalty + additionalPenalties;
  const finalPoints = Math.max(0, basePoints - totalPenalty);
  
  return {
    announcedDepth,
    registeredDepth,
    basePoints,
    underAPPenalty,
    tagPenalty,
    additionalPenalties,
    totalPenalty,
    finalPoints
  };
};

/**
 * Calculate late start penalty
 * @param {number} secondsLate - Seconds after the 10-second grace window
 * @returns {object} Penalty info or DQ
 */
export const calculateLateStartPenalty = (secondsLate) => {
  if (secondsLate <= 0) return { penalty: 0, isDQ: false };
  if (secondsLate > 20) return { penalty: 0, isDQ: true, reason: "DQ LATE START - More than 30 seconds after OT" };
  
  // 1 point per 5-second unit
  const penaltyUnits = Math.ceil(secondsLate / 5);
  return {
    penalty: penaltyUnits,
    isDQ: false,
    calculation: `${penaltyUnits} point(s) for ${secondsLate} seconds late`
  };
};

/**
 * Format time for display (seconds to MM'SS")
 * @param {number} totalSeconds - Time in seconds
 * @returns {string} Formatted time string (e.g., "5'08")
 */
export const formatTimeAIDA = (totalSeconds) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);
  return `${minutes}'${seconds.toString().padStart(2, '0')}"`;
};

/**
 * Get discipline info by code
 * @param {string} code - Discipline code (e.g., 'DYN', 'CWT')
 * @returns {object|null} Discipline information
 */
export const getAIDADisciplineInfo = (code) => {
  const poolDiscipline = AIDA_RULES.disciplines.pool[code];
  if (poolDiscipline) return { ...poolDiscipline, category: 'pool' };
  
  const depthDiscipline = AIDA_RULES.disciplines.depth[code];
  if (depthDiscipline) return { ...depthDiscipline, category: 'depth' };
  
  return null;
};

/**
 * Get depth interval based on announced depth
 * @param {number} announcedDepth - Depth in meters
 * @returns {number} Minimum interval in seconds
 */
export const getDepthInterval = (announcedDepth) => {
  if (announcedDepth > 100) return 600;  // 10 minutes
  if (announcedDepth >= 80) return 540;  // 9 minutes
  if (announcedDepth >= 50) return 480;  // 8 minutes
  return 420;                             // 7 minutes
};

/**
 * Check if performance qualifies as record (white card only)
 * @param {string} card - Card received (WHITE, YELLOW, RED)
 * @returns {boolean} Whether record is valid
 */
export const isAIDARecordValid = (card) => {
  return card === 'WHITE';
};

export default AIDA_RULES;
